import { useState } from 'react';

import {
    Exercise,
    LabelBody,
    LabelDisplay2,
    SecondaryButton,
    Spacer,
} from '../../components';

import { ExerciseData, ExerciseViewProps } from '../../exercises';

import {
    IntroModuleExercise3Illustration1,
    IntroModuleExercise3Illustration2,
} from '../../icons';

import { ThermometerSelector } from '../../../thermometer/Views/ThermometerSelector';
import { ThermometerFeedback } from '../../../thermometer/Views/ThermometerFeedback';
import { IntroModuleExercise3NotificationSettings } from './IntroModuleExercise3NotificationSettings';
import {
    cancelWeeklyCheckInNotification,
    scheduleWeeklyCheckInNotification,
} from '../../nativeAPI';
import { useNavigation } from '../../useNavigation';
import { normalizeInput } from '../../useSyncedState';
import { ExerciseStatus } from '../../interfaces';
import { reportExerciseProgressAtom } from '../../data';
import { useSetAtom } from 'jotai';
import { createThermometerEntry } from '../../../thermometer/thermometerAPI';

const notificationDayOfWeekKey = 'notificationDayOfWeek';
const notificationTimeKey = 'notificationTime';
const notificationEnabledKey = 'notificationEnabled';
export const exerciseData93: ExerciseData = {
    ExerciseView: IntroModuleExercise3,
    dataKeys: [
        notificationDayOfWeekKey,
        notificationTimeKey,
        notificationEnabledKey,
    ],
    questions: [],
    closeConfirmation: {
        title: 'Vil du forlade dit tjek-in uden at gemme?',
        body: 'Din temperatur og evt. kommentar er først gemt, når du trykker “Gem og afslut”.',
        confirmText: 'Forlad',
        cancelText: 'Bliv',
    },
};

function IntroModuleExercise3({ exercise }: ExerciseViewProps) {
    const navigate = useNavigation();
    const [page, setPage] = useState(0);
    const [selectedLevel, setSelectedLevel] = useState(0);
    const [text, setText] = useState('');
    const reportExerciseProgress = useSetAtom(reportExerciseProgressAtom);

    const handleNotificationSettingsChanged = async (settings: {
        dayOfWeek: number | null;
        time: string;
        enabled: boolean;
    }) => {
        await reportExerciseProgress({
            exercise,
            updateRequest: {
                CurrentPage: exercise.CurrentPage,
                Data: {
                    [notificationDayOfWeekKey]: normalizeInput(
                        settings.dayOfWeek?.toString() ?? '',
                    ),
                    [notificationTimeKey]: normalizeInput(settings.time),
                    [notificationEnabledKey]: settings.enabled.toString(),
                },
                UpdatedStatus: ExerciseStatus.InProgress,
            },
        });

        if (!settings.enabled) {
            cancelWeeklyCheckInNotification();
            return;
        }

        const [hourString, minuteString] = settings.time.split(':');
        const hour = Number(hourString);
        const minute = Number(minuteString);

        scheduleWeeklyCheckInNotification(hour, minute, settings.dayOfWeek);
    };

    const notificationEnabled =
        (exercise.Data[notificationEnabledKey]?.toLowerCase() ?? 'true') ===
        'true';

    const savedDayOfWeek = exercise.Data[notificationDayOfWeekKey];
    const notificationDayOfWeek =
        savedDayOfWeek != null && savedDayOfWeek !== ''
            ? Number(savedDayOfWeek)
            : null;

    const notificationTime = exercise.Data[notificationTimeKey] ?? '';
    const handleContinueFromThermometer = async () => {
        await reportExerciseProgress({
            exercise,
            updateRequest: {
                CurrentPage: exercise.CurrentPage,
                Data: {
                    ...exercise.Data,
                    pressureLevel: normalizeInput(String(selectedLevel)),
                },
                UpdatedStatus: ExerciseStatus.InProgress,
            },
        });

        setPage(2);
    };

    const handleFinishThermometer = async () => {
        await createThermometerEntry({
            PressureLevel: selectedLevel,
            Text: text,
        });

        navigate.toModule();
    };

    return (
        <Exercise title="Tjek ind med dig selv">
            {page === 0 && (
                <>
                    <IntroModuleExercise3Illustration1 className="mx-auto my-40" />

                    <LabelDisplay2>Temperaturmåling</LabelDisplay2>

                    <LabelBody class="mt-10">
                        Vi vil tilbyde dig at sende notifikationer på vilkårlige
                        tidspunkter, så du kan tage temperaturen på, hvor
                        stresset du er. Det kan du bruge til at følge din
                        udvikling. Når du er færdig med modul fire, viser vi dig
                        en graf over alle dine målinger.
                    </LabelBody>

                    <LabelBody class="mt-10">
                        Når du tjekker ind med dig selv, begynder du at se
                        mønstre: Hvornår føler du dig mest stresset? Hvad sker
                        der omkring dig på de dage? Jo bedre du forstår det, jo
                        bedre kan du tage hånd om det.
                    </LabelBody>

                    <LabelBody class="mt-10">
                        Vi anbefaler at tjekke ind med dig selv her i appen en
                        gang om ugen og dine “temperaturmålinger” bliver gemt i
                        logbogen.
                    </LabelBody>

                    <IntroModuleExercise3Illustration2 className="mx-auto mt-40" />

                    <Spacer />

                    <SecondaryButton class="mt-30" onClick={() => setPage(1)}>
                        Videre til temperaturtjek
                    </SecondaryButton>
                </>
            )}

            {page === 1 && (
                <>
                    <ThermometerSelector
                        selected={selectedLevel}
                        onChange={setSelectedLevel}
                        showLogbookInfo={false}
                    />

                    <Spacer />

                    <SecondaryButton
                        class="mt-30"
                        disabled={selectedLevel === 0}
                        onClick={handleContinueFromThermometer}
                    >
                        Fortsæt
                    </SecondaryButton>
                </>
            )}

            {page === 2 && (
                <>
                    <IntroModuleExercise3NotificationSettings
                        initialEnabled={notificationEnabled}
                        initialDayOfWeek={notificationDayOfWeek}
                        initialTime={notificationTime}
                        onSave={handleNotificationSettingsChanged}
                    />
                    <>
                        <ThermometerFeedback
                            pressureLevel={selectedLevel}
                            text={text}
                            setText={setText}
                        />

                        <Spacer />

                        <SecondaryButton
                            class="mt-30"
                            onClick={handleFinishThermometer}
                        >
                            Gem og afslut
                        </SecondaryButton>
                    </>
                </>
            )}
        </Exercise>
    );
}
